<template>
  <div class="CustomerData">
    <v-row class="header d-flex">
      <v-col class="col-12 col-sm-8 text-sm-left text-center">
        <div class="title">{{ $t('crm.details-data.title') }}</div>
        <div class="subtitle">{{ $t('crm.details-data.subtitle') }}</div>
      </v-col>

      <v-col class="col-12 col-sm-4 text-sm-right text-center">
        <TextInput
          v-model="search"
          class="custom-placeholder"
          :placeholder="$t('crm.details-data.search')"
          icon="mdi-magnify"
        />
      </v-col>
    </v-row>
    <v-divider class="header-divider mt-8" />
    <v-row class="content mb-8">
      <AlertsDrawer
        :alerts="adminFormAlerts"
        :errors="adminFormErrors"
      />
      <v-data-table
        :headers="headers"
        :items="items"
        item-key="label"
        :search="search"
        class="attachments-table"
        :no-data-text="$t('common.no-data')"
        :no-results-text="$t('common.no-records-found')"
        hide-default-footer
        disable-pagination
      >
        <template v-slot:[`item.label`]="{ item }">
          <div class="label">
            {{ $t(`main.enums.fields.${item.label}`) }}
          </div>
        </template>
        <template v-slot:[`item.type`]="{ item }">
          <div class="type">
            {{ $t(`main.enums.sections.${item.type}`) }}
          </div>
        </template>
        <template v-slot:[`item.value`]="{ item }">
          <DisplayCell
            v-model="item.value"
            :field="item.label"
            :disabled="editMode"
            :type="item.type"
            @resetField="resetField"
            @editMode="toggleEditMode"
          />
        </template>
        <template v-slot:[`item.valid-data`]="{ item }">
          <div
            v-if="item.control"
            class="d-flex justify-space-between my-2"
          >
            <SwitchInput
              v-if="buttonsAreVisible"
              v-model="item.valid"
              :class="{ 'disabled-cell': editMode }"
              :options="switchOptions"
              @input="validateField(item.label, item.valid)"
            />
            <div v-else>
              {{ $t(`main.enums.status.${customerFileStatus}`) }}
            </div>
          </div>
        </template>
      </v-data-table>
      <div class="custom-footer pa-4 d-flex">
        <div class="flex-grow-1 text-left">
          <span class="footer-length"> {{ currentFields.length }}</span>
          <span> {{ $t('crm.details-attachement.data') }}</span>
        </div>
        <div class="d-flex justify-center">
          <v-btn
            v-show="showValidationBtns"
            text
            class="theme-btn app-button-primary color nice-padding mr-6"
            @click="validateData"
          >{{ $t('common.action.save-checks') }}</v-btn>
          <v-btn
            v-show="showValidationBtns"
            text
            outlined
            class="theme-btn app-button-secondary nice-padding mr-6"
            @click="cancelValidation"
          >{{ $t('common.action.cancel') }}</v-btn>
          <v-btn
            v-if="sendCustomerFileButtonIsVisible"
            text
            class="theme-btn app-button-primary color nice-padding"
            :disabled="!isReadyToValidate"
            @click="sendCustomerFile"
          >{{ $t('common.action.send-customer-file') }}</v-btn>
        </div>
      </div>
    </v-row>
  </div>
</template>

<script>
  import TextInput from '@/components/ui/inputs/TextInput.vue'
  import SwitchInput from '@/components/ui/inputs/SwitchInput.vue'
  import DisplayCell from '@/components/ui/table/DisplayCell.vue'
  import AlertsDrawer from '@/pages/CRM/CustomerDetails/components/AlertsDrawer.vue'
  import CommonMixin from '@/mixins/store/CommonMixin'
  import AdminMixin from '@/mixins/store/AdminMixin'
  import ApiService from '@/services/ApiService'
  import RequestAPI from '@/services/RequestAPI'
  import { AUTHORIZATIONS, PERSON_STATUS } from '@/services/models.const'

  export default {
    components: {
      TextInput,
      SwitchInput,
      DisplayCell,
      AlertsDrawer,
    },
    mixins: [CommonMixin, AdminMixin,],
    data() {
      return {
        editMode: false,
        showValidationBtns: false,
        search: '',
        items: [],
        switchOptions: [
          { key: false, label: 'Non', },
          { key: true, label: 'Oui', },
        ],
        headers: [
          {
            text: `${this.$t('crm.details-data.label')}`,
            value: 'label',
            class: 'attachement-type',
            width: '25%',
          },
          {
            text: `${this.$t('crm.details-data.type')}`,
            value: 'type',
            width: '20%',
          },
          {
            text: `${this.$t('crm.details-data.value')}`,
            value: 'value',
            width: '45%',
          },
          {
            text: `${this.$t('crm.details-data.valid-data')}`,
            value: 'valid-data',
            width: '10%',
          },
        ],
        documents: [
          {
            code: 'id_card',
            label: `${this.$t('main.enums.documents.id_card')}`,
          },
          {
            code: 'proof_of_address',
            label: `${this.$t('main.enums.documents.proof_of_address')}`,
          },
          {
            code: 'bank_account_identity',
            label: `${this.$t('main.enums.documents.bank_account_identity')}`,
          },
          {
            code: 'tax_self_certification',
            label: `${this.$t('main.enums.documents.tax_self_certification')}`,
          },
          {
            code: 'proof_of_tax_address',
            label: `${this.$t('main.enums.documents.proof_of_tax_address')}`,
          },
          {
            code: 'guardianship_document',
            label: `${this.$t('main.enums.documents.guardianship_document')}`,
          },
        ],
        localChecks: {},
      }
    },
    computed: {
      buttonsAreVisible() {
        return this.authorization !== AUTHORIZATIONS.readOnly
      },
      sendCustomerFileButtonIsVisible() {
        return this.currentCustomerFile.status === PERSON_STATUS.validated_by_user
      },
    },
    watch: {
      currentFields: function(val) {
        this.loadData()
      },
    },
    mounted() {
      this.loadData()
    },
    methods: {
      loadData() {
        this.items = JSON.parse(JSON.stringify(this.currentFields))
      },
      async sendCustomerFile() {
        this.toggleAppLoader(true)
        await this.refreshCustomerFields()

        if (this.currentCustomerFile) {
          const result = await RequestAPI.postAdvisorFinalValidation(this.currentCustomerFile.id)
          if (result.errors) {
            this.$message({
              type: 'error',
              text: 'send_file_customer_error',
            })
          } else {
            this.$message({
              type: 'success',
              text: 'send_file_customer_success',
            })
            await this.refreshCustomerFields()
          }
          this.toggleAppLoader(false)
        } else if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('No customer file')
        }
        this.toggleAppLoader(false)
      },
      async validateField(field, valid) {
        this.localChecks[field] = valid
        this.showValidationBtns = true
      },
      cancelValidation() {
        this.showValidationBtns = false
        this.loadData()
      },
      async validateData(field, valid) {
        this.toggleAppLoader(true)
        const payload = {
          type: 'person_controls',
          attributes: {},
        }
        payload.attributes = this.localChecks
        const result = await ApiService.updateAdvisorCheck(this.personControlId, payload)
        this.$store.commit('admin/setCurrentControls', result)
        this.refreshCustomerFields()
        this.showValidationBtns = false
        this.toggleAppLoader(false)
      },
      resetField(field) {
        const itemsIndex = this.items.findIndex((obj) => obj.label === field)
        const originalItemsIndex = this.currentFields.findIndex((obj) => obj.label === field)
        this.items[itemsIndex].value = this.currentFields[originalItemsIndex].value
      },
      toggleEditMode(value) {
        this.editMode = value
      },
    },
  }
</script>

<style lang="scss">
.CustomerData {
  height: 100%;
  .header {
    .title {
      color: $color-001c4b;
      font-size: 1.25em;
      font-weight: 500;
      line-height: 2em;
    }
    .subtitle {
      color: $color-4d4d4f;
      font-size: 1em;
      letter-spacing: 0.24px;
      line-height: 1.5em;
    }
  }
  .header-divider {
    background: rgba(158, 159, 162, 0.32);
    margin-bottom: 47px;
  }
  .content {
    padding-left: 10px;
    .attachments-table {
      width: 100%;
      .v-data-table__wrapper
      > table
      > tbody
      > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
        background: $color-transparent;
      }
      .v-data-table__mobile-row {
        display: contents;
      }
      tbody > tr:nth-of-type(even) {
        background-color: $color-fafbff;
      }
      td,
      th {
        font-size: 1rem;
        font-weight: 500;
        border-bottom: 1px solid $color-217_223_255 !important;
      }
      th {
        color: $color-009ee0 !important;
        text-transform: uppercase;
        font-weight: 500;
      }
    }
  }
  .custom-footer {
    width: 100%;
    border-bottom: 1px solid $color-217_223_255;
    text-transform: uppercase;
    font-size: 0.7em;
    .footer-length {
      color: $color-1976d2;
    }
  }
}
.disabled-cell {
  opacity: 0.5;
  pointer-events: none;
}
.custom-placeholder input::placeholder {
  color: $color-004f9f !important;
}
.label {
  color: $color-001c4b;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.24px;
  line-height: 1.5rem;
}
.type {
  color: $color-4d4d4f;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.24px;
  line-height: 1.5rem;
}
</style>
